/* eslint-disable max-lines */
import type {
  InfiniteData,
  QueryClient,
  UndefinedInitialDataInfiniteOptions,
  UndefinedInitialDataOptions} from '@tanstack/react-query';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { readContract } from '@wagmi/core';

import type { Address } from 'viem';

import type {
  DeserializedSelfAllocation,
  DeserializedSelfAllocations,
  DeserializedSelfPool,
  DeserializedStandaloneVesting,
  LaunchpadProject,
  LaunchpadSalePoolType,
  NormalizedFeatureToggles} from '@api';
import {
  getBanners,
  getCampaignsProjects,
  getKYCAccessToken,
  getPoolDepositFee,
  getSelfActiveSalesPools,
  getSelfActiveWhitelistingPools,
  getSelfAllocations,
  getSelfClaimablePools,
  getSelfClosedPools,
  getSelfStandaloneVestings,
  getSelfWhitelistPoolJoiningCriteriaResults,
  LAUNCHPAD_SALE_POOL_TYPE,
  POOLS_DEFAULT_PAGE_LIMIT,
  subscribeToLaunchpadNewsletter,
  verifySelfPoolWhitelist,
} from '@api';

// eslint-disable-next-line deprecate/import
import { wagmiConfig, type WagmiConfigChain } from '@shared/common/providers/LegacyWeb3Provider/wagmi';
import {
  LaunchpadVestingV1__factory,
  LaunchpadVestingV2__factory,
  LaunchpadVestingV3__factory,
} from '@shared/config/contracts/abi-typed';
import type { ContractInfo } from '@shared/constants/wallets';

import { getQueryClient } from '@utilities/react-query';

import { AUTH_USER_QUERY_KEY } from './keys';
import { launchpadProjectKey } from './launchpadProjects';

export const launchpadQueryKeys = {
  selfAllocations: [AUTH_USER_QUERY_KEY, 'selfAllocations'],
  selfStandaloneVestingAllocations: [AUTH_USER_QUERY_KEY, 'selfStandaloneVestingAllocations'],
  fcfsAllocationsBySalePoolId: (salePoolId: string) => [
    AUTH_USER_QUERY_KEY,
    'fcfsAllocationsBySalePoolId',
    salePoolId,
  ],
  proRataAllocationsBySalePoolId: (salePoolId: string) => [
    AUTH_USER_QUERY_KEY,
    'proRataAllocationsBySalePoolId',
    salePoolId,
  ],
  proRataSqrpGatedAllocationsBySalePoolId: (salePoolId: string) => [
    AUTH_USER_QUERY_KEY,
    'proRataSqrpGatedAllocationsBySalePoolId',
    salePoolId,
  ],
  sqrpGatedAllocationsBySalePoolId: (salePoolId: string) => [
    AUTH_USER_QUERY_KEY,
    'sqrpGatedAllocationsBySalePoolId',
    salePoolId,
  ],
  getLaunchpadCurrentUser: [AUTH_USER_QUERY_KEY, 'getLaunchpadCurrentUser'],
  getKYCToken: (params: Parameters<typeof getKYCAccessToken>[0]['json']) => [
    AUTH_USER_QUERY_KEY,
    'getKYCToken',
    params,
  ],
  selfActiveWhitelistingPools: [AUTH_USER_QUERY_KEY, 'selfActiveWhitelistingPools'],
  selfActiveWhitelistingPoolsInfinite: [AUTH_USER_QUERY_KEY, 'selfActiveWhitelistingPoolsInfinite'],
  selfActiveSalesPools: [AUTH_USER_QUERY_KEY, 'selfActiveSalesPools'],
  selfActiveSalesPoolsInfinite: [AUTH_USER_QUERY_KEY, 'selfActiveSalesPoolsInfinite'],
  selfClaimablePools: [AUTH_USER_QUERY_KEY, 'selfClaimablePools'],
  selfSelfStandaloneVestings: (walletAddress?: string) => [
    AUTH_USER_QUERY_KEY,
    'selfSelfStandaloneVestings',
    walletAddress,
  ],
  selfClaimablePoolsInfinite: (walletAddress?: string) => [
    AUTH_USER_QUERY_KEY,
    'selfClaimablePoolsInfinite',
    walletAddress,
  ],
  selfClosedPools: [AUTH_USER_QUERY_KEY, 'selfClosedPools'],
  selfClosedPoolsInfinite: [AUTH_USER_QUERY_KEY, 'selfClosedPoolsInfinite'],
  poolWhitelistJoiningCriteriaResults: (poolId: string) => [
    AUTH_USER_QUERY_KEY,
    'poolWhitelistJoiningCriteriaResults',
    poolId,
  ],
  poolDepositFee: (poolId: string, amount: number) => [AUTH_USER_QUERY_KEY, poolId, amount],
};

type VestingAbi =
  | typeof LaunchpadVestingV1__factory.abi
  | typeof LaunchpadVestingV2__factory.abi
  | typeof LaunchpadVestingV3__factory.abi;

export enum LaunchpadVestingContractVersion {
  V1 = 1,
  V2 = 2,
  V3 = 3,
}

type LaunchpadVestingContract = Record<
  LaunchpadVestingContractVersion,
  Pick<ContractInfo<VestingAbi>, 'abi'>
>;

export const parseVestingVersion = (version: string): LaunchpadVestingContractVersion => {
  const major = Number(version.split('.')[0]);

  if (
    major === LaunchpadVestingContractVersion.V1 ||
    major === LaunchpadVestingContractVersion.V2 ||
    major === LaunchpadVestingContractVersion.V3
  ) {
    return major as LaunchpadVestingContractVersion;
  }

  throw new Error(`Unsupported vesting contract version: ${version}`);
};

export const LAUNCHPAD_VESTING_CONTRACTS_ABI: LaunchpadVestingContract = {
  [LaunchpadVestingContractVersion.V1]: {
    abi: LaunchpadVestingV1__factory.abi,
  },
  [LaunchpadVestingContractVersion.V2]: {
    abi: LaunchpadVestingV2__factory.abi,
  },
  [LaunchpadVestingContractVersion.V3]: {
    abi: LaunchpadVestingV3__factory.abi,
  },
};

export const useGetKYCAssessTokenQuery = (
  { sourceId }: Parameters<typeof getKYCAccessToken>[0]['json'],
  options?: Pick<
    UndefinedInitialDataOptions<
      Awaited<ReturnType<typeof getKYCAccessToken>>,
      unknown,
      unknown,
      any
    >,
    'enabled'
  >,
) => {
  return useQuery({
    queryKey: launchpadQueryKeys.getKYCToken({ sourceId }),
    queryFn: () =>
      getKYCAccessToken({
        json: {
          sourceId,
        },
      }),
    ...options,
  });
};

export const useSubscribeToLaunchpadNewsletterMutation = () => {
  return useMutation({
    mutationFn: (email: string) => subscribeToLaunchpadNewsletter({ json: { email } }),
  });
};

export const useSelfActiveWhitelistingPoolsInfiniteQuery = ({
  limit = POOLS_DEFAULT_PAGE_LIMIT,
  options,
}: {
  limit?: number;
  options?: Pick<
    UndefinedInitialDataInfiniteOptions<
      Awaited<ReturnType<typeof getSelfActiveWhitelistingPools>>,
      unknown,
      unknown,
      any,
      number
    >,
    'enabled'
  >;
} = {}) => {
  return useInfiniteQuery({
    queryKey: launchpadQueryKeys.selfActiveWhitelistingPoolsInfinite,
    queryFn: ({ pageParam: nextOffset }) =>
      getSelfActiveWhitelistingPools({ searchParams: { limit, offset: nextOffset } }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages = []) => {
      return lastPage.length === limit ? limit * pages.length : undefined;
    },
    staleTime: 0,
    ...options,
  });
};

export const useSelfActiveSalesPoolsInfiniteQuery = ({
  limit = POOLS_DEFAULT_PAGE_LIMIT,
  options,
}: {
  limit?: number;
  options?: Pick<
    UndefinedInitialDataInfiniteOptions<
      Awaited<ReturnType<typeof getSelfActiveSalesPools>>,
      unknown,
      unknown,
      any,
      number
    >,
    'enabled'
  >;
} = {}) => {
  return useInfiniteQuery({
    queryKey: launchpadQueryKeys.selfActiveSalesPoolsInfinite,
    queryFn: ({ pageParam: nextOffset }) =>
      getSelfActiveSalesPools({ searchParams: { limit, offset: nextOffset } }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages = []) =>
      lastPage.length === limit ? limit * pages.length : undefined,
    staleTime: 0,
    ...options,
  });
};

export interface ClaimInfo {
  amount: bigint | number;
  canClaim: boolean;
  claimed: bigint | number;
  claimCount: number;
  claimedAt: number;
  exist: boolean;
  available: bigint | number;
  remain: bigint | number;
  nextAvailable: bigint | number;
  nextClaimAt: number;
  canRefund: boolean;
  refunded: boolean;
}

type ClaimInfoResult = [ClaimInfo, bigint?, bigint?];

const getClaimInfo = (
  contractVersion: LaunchpadVestingContractVersion,
  claimContractAddress: Address,
  userWalletAddress: Address,
  chainId: WagmiConfigChain['id'],
): Promise<ClaimInfoResult> => {
  const { abi } = LAUNCHPAD_VESTING_CONTRACTS_ABI[contractVersion];

  const claimCallsMap: Record<LaunchpadVestingContractVersion, Array<() => Promise<unknown>>> = {
    [LaunchpadVestingContractVersion.V1]: [
      () =>
        readContract(wagmiConfig, {
          abi,
          address: claimContractAddress,
          functionName: 'fetchClaimInfo',
          chainId,
          args: [userWalletAddress],
        }),
      () => Promise.resolve(0),
      () => Promise.resolve(0),
    ],
    [LaunchpadVestingContractVersion.V2]: [
      () =>
        readContract(wagmiConfig, {
          abi,
          address: claimContractAddress,
          functionName: 'fetchClaimInfo',
          chainId,
          args: [userWalletAddress],
        }),
      () =>
        readContract(wagmiConfig, {
          abi,
          address: claimContractAddress,
          functionName: 'refundCloseDate',
          chainId,
        }),
      () =>
        readContract(wagmiConfig, {
          abi,
          address: claimContractAddress,
          functionName: 'refundStartDate',
          chainId,
        }),
    ],
    [LaunchpadVestingContractVersion.V3]: [
      () =>
        readContract(wagmiConfig, {
          abi,
          address: claimContractAddress,
          functionName: 'fetchClaimInfo',
          chainId,
          args: [userWalletAddress],
        }),
    ],
  };

  const calls = claimCallsMap[contractVersion] ?? claimCallsMap[2];

  return Promise.all(calls.map((fn) => fn())) as Promise<ClaimInfoResult>;
};

export const vestingFetchClaimInfo = async ({
  claimContractAddress,
  chainId,
  userWalletAddress,
}: {
  claimContractAddress: Address;
  chainId: WagmiConfigChain['id'];
  userWalletAddress: Address;
}) => {
  // version function is available fron V2
  const VERSION = await readContract(wagmiConfig, {
    abi: LAUNCHPAD_VESTING_CONTRACTS_ABI[LaunchpadVestingContractVersion.V2].abi,
    address: claimContractAddress as Address,
    functionName: 'VERSION',
    chainId,
  });
  const contractVersion = parseVestingVersion(VERSION);

  const [claimInfo, refundEndDate = 0, refundStartDate = 0] = await getClaimInfo(
    contractVersion,
    claimContractAddress,
    userWalletAddress,
    chainId,
  );

  const claimInfoResult = {
    amount: claimInfo?.amount || 0,
    canClaim: claimInfo?.canClaim || false,
    claimed: claimInfo?.claimed || 0,
    claimCount: claimInfo?.claimCount || 0,
    claimedAt: claimInfo?.claimedAt || 0,
    exist: claimInfo?.exist || false,
    available: claimInfo?.available || 0,
    remain: claimInfo?.remain || 0,
    nextAvailable: claimInfo?.nextAvailable || 0,
    nextClaimAt: claimInfo?.nextClaimAt || 0,
    canRefund: claimInfo?.canRefund || false,
    refunded: claimInfo?.refunded || false,
  };

  return {
    ...claimInfoResult,
    refundEndDate,
    refundStartDate,
  };
};

export interface DeserializedSelfPoolWithClaimInfo extends DeserializedSelfPool {
  claimInfo?: Awaited<ReturnType<typeof vestingFetchClaimInfo>>;
}

export interface DeserializedStandaloneVestingWithClaimInfo extends DeserializedStandaloneVesting {
  claimInfo?: Awaited<ReturnType<typeof vestingFetchClaimInfo>>;
}

export type ClaimableQueryKeyType = 'selfClaimablePoolsInfinite' | 'selfSelfStandaloneVestings';

export const setSelfClaimableInfiniteQueryData = (
  updated: (
    data?: InfiniteData<
      (DeserializedSelfPoolWithClaimInfo | DeserializedStandaloneVestingWithClaimInfo)[]
    >,
  ) =>
    | InfiniteData<
        (DeserializedSelfPoolWithClaimInfo | DeserializedStandaloneVestingWithClaimInfo)[]
      >
    | undefined,
  userWalletAddress?: Address,
  queryKeyType?: ClaimableQueryKeyType,
) => {
  const queryClient = getQueryClient();

  const keyMap = {
    selfClaimablePoolsInfinite: launchpadQueryKeys.selfClaimablePoolsInfinite(userWalletAddress),
    selfSelfStandaloneVestings: launchpadQueryKeys.selfSelfStandaloneVestings(userWalletAddress),
  };

  if (queryClient) {
    queryClient.setQueryData(keyMap[queryKeyType ?? 'selfClaimablePoolsInfinite'], updated);
  }
};

const fetchClaimInfo = async ({
  chainId,
  claimContractAddress,
  userWalletAddress,
}: {
  chainId: number;
  claimContractAddress: Address;
  userWalletAddress: Address;
}) => {
  try {
    return await vestingFetchClaimInfo({
      chainId,
      claimContractAddress,
      userWalletAddress,
    });
  } catch (error) {
    console.error(error);
    return undefined;
  }
};

const populatePoolsWithClaimInfo = (
  pools: DeserializedSelfPool[],
  userWalletAddress: Address,
): Promise<DeserializedSelfPoolWithClaimInfo[]> => {
  return Promise.all(
    pools.map(async (pool) => {
      if (!pool.claimContractAddress || !pool.project?.claimingBlockchainId) {
        return pool;
      }

      const claimInfo =
        (await fetchClaimInfo({
          chainId: pool.project.claimingBlockchainId,
          claimContractAddress: pool.claimContractAddress,
          userWalletAddress,
        })) || undefined;

      return {
        ...pool,
        claimInfo,
      };
    }),
  );
};

const populateVestingsWithClaimInfo = (
  vestings: DeserializedStandaloneVesting[],
  userWalletAddress: Address,
): Promise<DeserializedStandaloneVestingWithClaimInfo[]> => {
  return Promise.all(
    vestings.map(async (vesting) => {
      if (!vesting.address || !vesting.blockchainNetworkId) {
        return vesting;
      }

      const claimInfo = await fetchClaimInfo({
        chainId: vesting.blockchainNetworkId,
        claimContractAddress: vesting.address,
        userWalletAddress,
      });

      return {
        ...vesting,
        claimInfo,
      };
    }),
  );
};

export const useSelfClaimablePoolsInfiniteQuery = ({
  limit = POOLS_DEFAULT_PAGE_LIMIT,
  walletAddress,
  options,
}: {
  limit?: number;
  walletAddress: Address;
  options?: Pick<
    UndefinedInitialDataInfiniteOptions<
      Awaited<ReturnType<typeof getSelfClaimablePools>>,
      unknown,
      unknown,
      any,
      number
    >,
    'enabled'
  >;
}) => {
  return useInfiniteQuery({
    queryKey: launchpadQueryKeys.selfClaimablePoolsInfinite(walletAddress),
    queryFn: async ({ pageParam: nextOffset }): Promise<DeserializedSelfPoolWithClaimInfo[]> => {
      const claimablePools = await getSelfClaimablePools({
        searchParams: { limit, offset: nextOffset },
      });

      return populatePoolsWithClaimInfo(claimablePools, walletAddress);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages = []) =>
      lastPage.length === limit ? limit * pages.length : undefined,
    staleTime: 0,
    ...options,
  });
};

const STANDALONE_VESTINGS_LIMIT = 10;

export const useSelfStandaloneVestingsInfiniteQuery = ({
  limit = POOLS_DEFAULT_PAGE_LIMIT,
  walletAddress,
  options,
}: {
  limit?: number;
  walletAddress?: Address;
  options?: Pick<
    UndefinedInitialDataInfiniteOptions<
      Awaited<ReturnType<typeof getSelfStandaloneVestings>>,
      unknown,
      unknown,
      any,
      number
    >,
    'enabled'
  >;
}) => {
  return useInfiniteQuery({
    queryKey: launchpadQueryKeys.selfSelfStandaloneVestings(walletAddress),
    queryFn: async ({ pageParam: nextOffset }): Promise<DeserializedStandaloneVesting[]> => {
      if (!walletAddress) {
        return [];
      }

      const standaloneVestings = await getSelfStandaloneVestings({
        searchParams: { limit, offset: nextOffset },
      });

      return populateVestingsWithClaimInfo(standaloneVestings, walletAddress);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.length === STANDALONE_VESTINGS_LIMIT
        ? allPages.length * STANDALONE_VESTINGS_LIMIT
        : undefined;
    },
    staleTime: 0,
    ...options,
  });
};

export const useSelfClosedPoolsInfiniteQuery = ({
  limit = POOLS_DEFAULT_PAGE_LIMIT,
  walletAddress,
  options,
}: {
  limit?: number;
  walletAddress: Address;
  options?: Pick<
    UndefinedInitialDataInfiniteOptions<
      Awaited<ReturnType<typeof getSelfClosedPools>>,
      unknown,
      unknown,
      any,
      number
    >,
    'enabled'
  >;
}) => {
  return useInfiniteQuery({
    queryKey: launchpadQueryKeys.selfClosedPoolsInfinite,
    queryFn: async ({ pageParam: nextOffset }) => {
      const pools = await getSelfClosedPools({
        searchParams: { limit: limit, offset: nextOffset },
      });

      return populatePoolsWithClaimInfo(pools, walletAddress);
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages = []) =>
      lastPage.length === limit ? limit * pages.length : undefined,
    staleTime: 0,
    ...options,
  });
};

export const usePoolWhitelistJoiningCriteriaResultsQuery = (
  poolId: string,
  options?: Pick<
    UndefinedInitialDataOptions<
      Awaited<ReturnType<typeof getSelfWhitelistPoolJoiningCriteriaResults>>,
      unknown,
      unknown,
      any
    >,
    'enabled'
  >,
) => {
  return useQuery({
    queryKey: launchpadQueryKeys.poolWhitelistJoiningCriteriaResults(poolId),
    queryFn: () => getSelfWhitelistPoolJoiningCriteriaResults(poolId),
    ...options,
  });
};

export const useVerifySelfPoolWhitelistMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ poolId }: { poolId: string; projectId?: string }) =>
      verifySelfPoolWhitelist(poolId),
    onSuccess: (data, { poolId, projectId }) => {
      queryClient.setQueryData(
        launchpadQueryKeys.poolWhitelistJoiningCriteriaResults(poolId),
        data,
      );

      if (projectId) {
        const projectPoolKey = launchpadProjectKey.project(projectId, false);

        queryClient.setQueriesData(
          {
            predicate: (query) => {
              return (
                query.queryKey[0] === projectPoolKey[0] && query.queryKey[1] === projectPoolKey[1]
              );
            },
          },
          (oldData?: LaunchpadProject) => {
            if (!oldData) {
              return oldData;
            }

            return {
              ...oldData,
              salePool: oldData.salePool.map((pool) => {
                if (pool.id !== poolId) {
                  return pool;
                }

                return {
                  ...pool,
                  attributes: {
                    ...pool.attributes,
                    meta: {
                      ...pool.attributes.meta,
                      isWhitelistParticipant: data.meta.participationVerified,
                    },
                  },
                };
              }),
            };
          },
        );
      }

      const updateDashboardPools = (oldData?: InfiniteData<DeserializedSelfPool[]>) => {
        if (!oldData?.pages.length) {
          return oldData;
        }

        return {
          ...oldData,
          pages: oldData.pages.map((page) => {
            return page.map((pool) => {
              if (pool.id !== poolId) {
                return pool;
              }

              return {
                ...pool,
                meta: {
                  ...pool.meta,
                  whiteListContainsUser: data.meta.participationVerified,
                },
              };
            });
          }),
        };
      };

      queryClient.setQueryData(
        launchpadQueryKeys.selfActiveWhitelistingPoolsInfinite,
        updateDashboardPools,
      );

      queryClient.setQueryData(
        launchpadQueryKeys.selfActiveSalesPoolsInfinite,
        updateDashboardPools,
      );
    },
  });
};

export const useSelfAllocationsQuery = (
  options?: Pick<
    UndefinedInitialDataOptions<
      Awaited<ReturnType<typeof getSelfAllocations>>,
      unknown,
      unknown,
      any
    >,
    'enabled'
  >,
) => {
  return useQuery({
    queryKey: launchpadQueryKeys.selfAllocations,
    queryFn: () => getSelfAllocations(),
    staleTime: 0,
    ...options,
  });
};

export const prefetchSelfAllocationsQuery = (
  clientQuery: QueryClient,
  options?: Parameters<typeof getSelfAllocations>[0],
) => {
  return clientQuery.prefetchQuery({
    queryKey: launchpadQueryKeys.selfAllocations,
    queryFn: () => getSelfAllocations(options),
  });
};

const POOL_TYPE_TO_KEY = {
  [LAUNCHPAD_SALE_POOL_TYPE.FCFS]: 'fcfsAllocation',
  [LAUNCHPAD_SALE_POOL_TYPE.SQRP_GATED]: 'sqrpGatedAllocation',
  [LAUNCHPAD_SALE_POOL_TYPE.PRO_RATA]: 'proRataAllocation',
  [LAUNCHPAD_SALE_POOL_TYPE.PRO_RATA_SQRP_GATED]: 'proRataSqrpGatedAllocation',
} as const;

export const getAllocationsByPoolType = (
  allocations: DeserializedSelfAllocations,
  poolType: LaunchpadSalePoolType,
) => {
  return [...(allocations[POOL_TYPE_TO_KEY[poolType]] || [])];
};

export const getPoolAllocation = (
  allocations: DeserializedSelfAllocations | undefined,
  poolType: LaunchpadSalePoolType,
  poolId: string,
): DeserializedSelfAllocation | undefined => {
  const defaultData = { amount: 0, amountBought: 0, sqrpAmount: 0, id: '0', salePool: null };

  if (!allocations) {
    return defaultData;
  }

  const allocationsByPoolType = getAllocationsByPoolType(allocations, poolType);

  const data = allocationsByPoolType.find((allocation) => allocation?.salePool?.id === poolId);

  return data || defaultData;
};

export const useSelfPoolAllocationQuery = (
  { poolType, poolId }: { poolType: LaunchpadSalePoolType; poolId: string },
  options?: Pick<
    UndefinedInitialDataOptions<
      Awaited<ReturnType<typeof getPoolAllocation>>,
      unknown,
      unknown,
      any
    >,
    'enabled'
  >,
) => {
  const { data: userAllocations, refetch: refetchAllocations } = useSelfAllocationsQuery();

  const keys = [
    ...(userAllocations?.fcfsAllocation?.map((el) => el.id) || []),
    ...(userAllocations?.sqrpGatedAllocation?.map((el) => el.id) || []),
    ...(userAllocations?.proRataAllocation?.map((el) => el.id) || []),
    ...(userAllocations?.proRataSqrpGatedAllocation?.map((el) => el.id) || []),
    ...(userAllocations?.standaloneVestingAllocation?.map((el) => el.id) || []),
  ];

  const query = useQuery({
    queryKey: [...launchpadQueryKeys.selfAllocations, poolType, poolId, ...keys],
    queryFn: () => getPoolAllocation(userAllocations, poolType, poolId),
    staleTime: 0,
    ...options,
  });

  const refetchPoolAllocations = async () => {
    await refetchAllocations();
    await query.refetch();
  };

  return {
    ...query,
    refetchPoolAllocations,
  };
};

export const useUpdateSelfAllocationQuery = ({
  poolType,
  poolId,
}: {
  poolType: LaunchpadSalePoolType;
  poolId: string;
}) => {
  const queryClient = useQueryClient();
  const { data: userAllocations } = useSelfAllocationsQuery();

  const keys = [
    ...(userAllocations?.fcfsAllocation?.map((el) => el.id) || []),
    ...(userAllocations?.sqrpGatedAllocation?.map((el) => el.id) || []),
  ];

  return (sqrpUpdated: number) => {
    queryClient.setQueryData(
      [...launchpadQueryKeys.selfAllocations, poolType, poolId, ...keys],
      (oldData: DeserializedSelfAllocation | undefined) => {
        const result = oldData
          ? {
              ...oldData,
              sqrpAmount: (oldData?.sqrpAmount || 0) + sqrpUpdated || 0,
            }
          : oldData;
        return result;
      },
    );
  };
};

type GetPoolDepositFeeQueryParams = {
  poolId: string;
  amount: number;
  hasDepositFee: boolean;
};

export const useGetPoolDepositFeeQuery = ({
  poolId,
  amount,
  hasDepositFee,
}: GetPoolDepositFeeQueryParams) => {
  return useQuery({
    queryKey: launchpadQueryKeys.poolDepositFee(poolId, amount),
    queryFn: () => getPoolDepositFee(poolId, { searchParams: { amount } }),
    enabled: hasDepositFee && Boolean(poolId) && amount > 0,
  });
};

const bannersQueryKey = {
  server: ['mainPageBanners'],
  client: ['clientMainPageBanners'],
};

export const prefetchBannersQuery = async (clientQuery: QueryClient) => {
  await clientQuery.prefetchQuery({
    queryKey: bannersQueryKey.server,
    queryFn: () => getBanners(),
  });

  return clientQuery.getQueryData<NormalizedFeatureToggles>(bannersQueryKey.server);
};

export const useGetBannersQuery = () => {
  return useQuery({
    queryKey: bannersQueryKey.client,
    queryFn: () => getBanners(),
  });
};

export const useGetCampaignsProjectsQuery = () => {
  return useQuery({
    queryKey: ['campaignsProjects'],
    queryFn: () => getCampaignsProjects(),
  });
};
